import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { AffiliateDisclosure } from "@/components/site/OfferGrid";
import { AffiliateCardLink } from "@/components/site/AffiliateCardLink";

// Medical & Mobility only. Gives every page that has approved partners a shopping route at the top
// and bottom as well as the middle card grid, without duplicating the product cards themselves.
// Scoped to this section: it is not used anywhere else on Human Home Living.

export const useMedicalOffers = (category) => {
  const [offers, setOffers] = useState([]);
  useEffect(() => {
    let live = true;
    if (!category) return undefined;
    api.get("/offers/group", { params: { category } })
      .then((r) => live && setOffers(r.data.offers || []))
      .catch(() => live && setOffers([]));
    return () => {
      live = false;
    };
  }, [category]);
  return offers;
};

const CtaButton = ({ offer, placement, category }) => (
  <AffiliateCardLink
    href={offer.tracking_url}
    ariaLabel={`${offer.cta_label} at ${offer.merchant}`}
    testId={`mm-cta-${placement}-${offer.id}`}
    rel="sponsored noopener noreferrer"
    trackPayload={{
      event_type: "cta_click",
      cta_placement: placement,
      offer_category: category,
      offer_id: offer.id,
      merchant: offer.merchant,
      product: offer.display_name,
    }}
    className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-7 py-4 text-[12px] leading-tight tracking-[0.14em] uppercase font-semibold bg-sage text-white hover:bg-sage-dark transition-colors duration-300"
  >
    {offer.cta_label} <ArrowUpRight size={15} className="shrink-0" />
  </AffiliateCardLink>
);

// Sits directly under the page introduction, ahead of the overview and educational sections.
export const MedicalTopCta = ({ offers, category }) => {
  if (!offers.length) return null;
  return (
    <div
      data-testid="mm-top-cta"
      className="mt-8 md:mt-9 border-t border-line pt-7 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3"
    >
      <span className="text-[11px] tracking-[0.18em] uppercase text-sage-dark font-semibold sm:mr-2 self-start sm:self-center">
        Shop now
      </span>
      {offers.map((o) => (
        <CtaButton key={o.id} offer={o} category={category} placement="MEDICAL TOP CTA" />
      ))}
    </div>
  );
};

// Compact closing section: buttons only, no product imagery, after the educational content.
export const MedicalBottomCta = ({ offers, category, testId = "mm-bottom-cta" }) => {
  if (!offers.length) return null;
  return (
    <section
      data-testid={testId}
      className="mt-16 md:mt-20 border border-line bg-bone px-6 md:px-9 py-9 md:py-10"
    >
      <span className="eyebrow">Approved partners</span>
      <h2 className="display text-[25px] md:text-[31px] leading-[1.14] text-charcoal mt-4">
        Explore Equipment From Our Partners
      </h2>
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-7">
        {offers.map((o) => (
          <CtaButton key={o.id} offer={o} category={category} placement="MEDICAL BOTTOM CTA" />
        ))}
      </div>
      <AffiliateDisclosure testId={`${testId}-disclosure`} className="mt-7 max-w-3xl" />
    </section>
  );
};
